import React, { useState, useEffect } from 'react';
import { Modal, Button } from "react-bootstrap";
import axios from "axios";
import config from '../../Common/Configurations/APIConfig';
import AdminHeader from '../../Common/Layout/AdminHeader';
import { rexTechTheme } from '../../Common/Theme';
import { 
  Recycle,
  Edit2,
  Trash2,
  Plus,
  Search,
  Filter,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';


const initialFieldValues = {
    recycleItemId: '00000000-0000-0000-0000-000000000000',
    recycleItemName: ''
};

const RecycleItems = () => {
    const [lists, setLists] = useState([]);
    const [filteredLists, setFilteredLists] = useState([]);
    const [btnLoading, setBtnLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [values, setValues] = useState(initialFieldValues);
    const [errors, setErrors] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        GetData();
    }, []);

    useEffect(() => {
        const filtered = lists.filter(item =>
            item.recycleItemName.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredLists(filtered);
    }, [searchTerm, lists]);

    // ================= API Calls =================

    const GetData = async () => {
        try {
            setIsLoading(true);
            const res = await axios.get(config.APIACTIVEURL + config.GETALLRECYCLEITEMS);
            setLists(res.data || []);
            setFilteredLists(res.data || []);
        } catch (error) {
            console.error("Error fetching recycle items", error);
            showToast('error', 'Failed to load recycle items');
        } finally {
            setIsLoading(false);
        }
    };

    const showToast = (type, message) => {
        // Implement toast notification here
        console.log(`${type}: ${message}`);
    };

    // ================= Form Handling =================

    const handleInput = (e) => {
        const { name, value } = e.target;
        setValues({ ...values, [name]: value });
        if (errors[name]) {
            setErrors({ ...errors, [name]: '' });
        }
    };

    const validate = () => {
        let temp = {};
        temp.recycleItemName = values.recycleItemName ? "" : "Recycle Item Name is required";
        
        if (values.recycleItemName && values.recycleItemName.length < 2) {
            temp.recycleItemName = "Name must be at least 2 characters";
        }
        
        if (values.recycleItemName && values.recycleItemName.length > 20) {
            temp.recycleItemName = "Name must not exceed 20 characters";
        }

        setErrors(temp);
        return Object.values(temp).every(x => x === "");
    };

    // ================= CRUD Operations =================

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setBtnLoading(true);

        try {
            const isNew = values.recycleItemId === '00000000-0000-0000-0000-000000000000';
            const url = isNew 
                ? config.APIACTIVEURL + config.CREATERECYCLEITEM
                : config.APIACTIVEURL + config.UPDATERECYCLEITEM;

            const response = isNew
                ? await axios.post(url, values)
                : await axios.put(url, values);

            if (response.status === 200 || response.status === 201) {
                showToast('success', `Recycle item ${isNew ? 'created' : 'updated'} successfully`);
                setShowModal(false);
                resetForm();
                GetData();
            }
        } catch (error) {
            console.error("Save failed", error);
            showToast('error', `Failed to ${values.recycleItemId === '00000000-0000-0000-0000-000000000000' ? 'create' : 'update'} recycle item`);
        } finally {
            setBtnLoading(false);
        }
    };

    const handleEdit = (item) => {
        setValues({
            recycleItemId: item.recycleItemId,
            recycleItemName: item.recycleItemName
        });
        setErrors({});
        setShowModal(true);
    };

    const handleDelete = async () => {
        try {
            await axios.delete(config.APIACTIVEURL + config.DELETERECYCLEITEM + "/" + itemToDelete);
            showToast('success', 'Recycle item deleted successfully');
            setShowDeleteModal(false);
            GetData();
        } catch (error) {
            console.error("Delete failed", error);
            showToast('error', 'Failed to delete recycle item');
        }
    };

    const resetForm = () => {
        setValues(initialFieldValues);
        setErrors({});
    };

    // ================= UI Components =================

    const LoadingSpinner = () => (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: rexTechTheme.spacing.xxl,
            color: rexTechTheme.colors.primary.main
        }}>
            <div style={{
                width: '40px',
                height: '40px',
                border: `3px solid ${rexTechTheme.colors.primary.lightest}`,
                borderTop: `3px solid ${rexTechTheme.colors.primary.main}`,
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                marginBottom: rexTechTheme.spacing.md
            }} />
            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
            <span style={{ color: rexTechTheme.colors.neutral.darkGray }}>
                Loading recycle items...
            </span>
        </div>
    );

    const EmptyState = () => (
        <div style={{
            textAlign: 'center',
            padding: rexTechTheme.spacing.xxl,
            color: rexTechTheme.colors.neutral.darkGray
        }}>
            <Recycle size={48} style={{ 
                marginBottom: rexTechTheme.spacing.md,
                color: rexTechTheme.colors.neutral.gray,
                opacity: 0.5
            }} />
            <h3 style={{ 
                color: rexTechTheme.colors.neutral.darkGray,
                marginBottom: rexTechTheme.spacing.sm
            }}>
                No Recycle Items Found
            </h3>
            <p style={{ marginBottom: rexTechTheme.spacing.lg }}>
                Get started by adding your first recycle item
            </p>
            <button
                onClick={() => { resetForm(); setShowModal(true); }}
                style={{
                    padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.xl}`,
                    background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.main} 0%, ${rexTechTheme.colors.primary.light} 100%)`,
                    color: rexTechTheme.colors.neutral.white,
                    border: 'none',
                    borderRadius: rexTechTheme.borderRadius.medium,
                    fontSize: rexTechTheme.typography.fontSize.regular,
                    fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: rexTechTheme.spacing.sm,
                    margin: '0 auto',
                    boxShadow: '0 4px 15px rgba(30, 132, 73, 0.3)',
                    ':hover': {
                        background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.dark} 0%, ${rexTechTheme.colors.primary.main} 100%)`,
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px rgba(30, 132, 73, 0.4)'
                    }
                }}
            >
                <Plus size={18} />
                Add First Item
            </button>
        </div>
    );

    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: rexTechTheme.colors.neutral.lightGray,
            fontFamily: rexTechTheme.typography.fontFamily
        }}>
            <AdminHeader />
            
            <div style={{
                maxWidth: '1400px',
                margin: '0 auto',
                padding: rexTechTheme.spacing.xl
            }}>
                {/* Header Section */}
                <div style={{
                    marginBottom: rexTechTheme.spacing.xl
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: rexTechTheme.spacing.lg
                    }}>
                        <div>
                            <h1 style={{
                                fontSize: rexTechTheme.typography.fontSize.xxlarge,
                                color: rexTechTheme.colors.primary.dark,
                                fontWeight: rexTechTheme.typography.fontWeight.bold,
                                marginBottom: rexTechTheme.spacing.xs
                            }}>
                                Recycle Items 
                            </h1>
                           
                        </div>
                        
                        <button
                            onClick={() => { resetForm(); setShowModal(true); }}
                            style={{
                                padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.xl}`,
                                background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.main} 0%, ${rexTechTheme.colors.primary.light} 100%)`,
                                color: rexTechTheme.colors.neutral.white,
                                border: 'none',
                                borderRadius: rexTechTheme.borderRadius.medium,
                                fontSize: rexTechTheme.typography.fontSize.regular,
                                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: rexTechTheme.spacing.sm,
                                boxShadow: '0 4px 15px rgba(30, 132, 73, 0.3)',
                                ':hover': {
                                    background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.dark} 0%, ${rexTechTheme.colors.primary.main} 100%)`,
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 6px 20px rgba(30, 132, 73, 0.4)'
                                }
                            }}
                        >
                            <Plus size={18} />
                            Add New Item
                        </button>
                    </div>

                    {/* Search and Filter */}
                    <div style={{
                        backgroundColor: rexTechTheme.colors.neutral.white,
                        borderRadius: rexTechTheme.borderRadius.large,
                        padding: rexTechTheme.spacing.lg,
                        boxShadow: rexTechTheme.shadows.light,
                        border: `1px solid ${rexTechTheme.colors.neutral.gray}`
                    }}>
                        <div style={{
                            display: 'flex',
                            gap: rexTechTheme.spacing.md,
                            alignItems: 'center'
                        }}>
                            <div style={{ position: 'relative', flex: 1 }}>
                                <Search 
                                    size={20} 
                                    style={{
                                        position: 'absolute',
                                        left: rexTechTheme.spacing.md,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: rexTechTheme.colors.neutral.darkGray
                                    }}
                                />
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Search recycle items..."
                                    style={{
                                        width: '100%',
                                        padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.md} ${rexTechTheme.spacing.md} ${rexTechTheme.spacing.xl}`,
                                        paddingLeft: `calc(${rexTechTheme.spacing.xl} + 20px)`,
                                        border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                                        borderRadius: rexTechTheme.borderRadius.medium,
                                        fontSize: rexTechTheme.typography.fontSize.regular,
                                        transition: 'all 0.2s ease',
                                        outline: 'none',
                                        boxSizing: 'border-box',
                                        ':focus': {
                                            borderColor: rexTechTheme.colors.primary.main,
                                            boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                                        }
                                    }}
                                />
                            </div>
                            
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: rexTechTheme.spacing.sm,
                                color: rexTechTheme.colors.neutral.darkGray,
                                fontSize: rexTechTheme.typography.fontSize.small
                            }}>
                                <Filter size={16} />
                                <span>{filteredLists.length} items found</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div style={{
                    backgroundColor: rexTechTheme.colors.neutral.white,
                    borderRadius: rexTechTheme.borderRadius.large,
                    boxShadow: rexTechTheme.shadows.light,
                    border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                    overflow: 'hidden'
                }}>
                    {isLoading ? (
                        <LoadingSpinner />
                    ) : filteredLists.length === 0 ? (
                        <EmptyState />
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{
                                width: '100%',
                                borderCollapse: 'collapse',
                                minWidth: '600px'
                            }}>
                                <thead>
                                    <tr style={{
                                        backgroundColor: rexTechTheme.colors.neutral.lightGray,
                                        borderBottom: `1px solid ${rexTechTheme.colors.neutral.gray}`
                                    }}>
                                        
                                        <th style={{
                                            padding: rexTechTheme.spacing.lg,
                                            textAlign: 'left',
                                            fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                            color: rexTechTheme.colors.primary.dark,
                                            fontSize: rexTechTheme.typography.fontSize.regular,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Recycle Item Name
                                        </th>
                                        <th style={{
                                            padding: rexTechTheme.spacing.lg,
                                            textAlign: 'center',
                                            fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                            color: rexTechTheme.colors.primary.dark,
                                            fontSize: rexTechTheme.typography.fontSize.regular,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            width: '150px'
                                        }}>
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredLists.map((item, index) => (
                                        <tr 
                                            key={item.recycleItemId}
                                            style={{
                                                backgroundColor: index % 2 === 0 
                                                    ? rexTechTheme.colors.neutral.white 
                                                    : rexTechTheme.colors.neutral.lightGray,
                                                borderBottom: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                                                transition: 'all 0.2s ease',
                                                ':hover': {
                                                    backgroundColor: rexTechTheme.colors.primary.lightest
                                                }
                                            }}
                                        >
                                           
                                            <td style={{
                                                padding: rexTechTheme.spacing.lg,
                                                color: rexTechTheme.colors.primary.dark,
                                                fontSize: rexTechTheme.typography.fontSize.regular,
                                                fontWeight: rexTechTheme.typography.fontWeight.medium
                                            }}>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: rexTechTheme.spacing.md
                                                }}>
                                                    <div style={{
                                                        width: '40px',
                                                        height: '40px',
                                                        backgroundColor: rexTechTheme.colors.primary.lightest,
                                                        borderRadius: '50%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}>
                                                        <Recycle size={18} color={rexTechTheme.colors.primary.main} />
                                                    </div>
                                                    {item.recycleItemName}
                                                </div>
                                            </td>
                                            <td style={{
                                                padding: rexTechTheme.spacing.lg,
                                                textAlign: 'center'
                                            }}>
                                                <div style={{
                                                    display: 'flex',
                                                    gap: rexTechTheme.spacing.xs,
                                                    justifyContent: 'center'
                                                }}>
                                                    <button
                                                        onClick={() => handleEdit(item)}
                                                        style={{
                                                            padding: rexTechTheme.spacing.sm,
                                                            backgroundColor: rexTechTheme.colors.primary.lightest,
                                                            border: `1px solid ${rexTechTheme.colors.primary.light}`,
                                                            color: rexTechTheme.colors.primary.main,
                                                            borderRadius: rexTechTheme.borderRadius.small,
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s ease',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            ':hover': {
                                                                backgroundColor: rexTechTheme.colors.primary.light,
                                                                color: rexTechTheme.colors.neutral.white
                                                            }
                                                        }}
                                                    >
                                                        <Edit2 size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setItemToDelete(item.recycleItemId);
                                                            setShowDeleteModal(true);
                                                        }}
                                                        style={{
                                                            padding: rexTechTheme.spacing.sm,
                                                            backgroundColor: rexTechTheme.colors.primary.lightest,
                                                            border: `1px solid ${rexTechTheme.colors.accent.orange}`,
                                                            color: rexTechTheme.colors.accent.orange,
                                                            borderRadius: rexTechTheme.borderRadius.small,
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s ease',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            ':hover': {
                                                                backgroundColor: rexTechTheme.colors.accent.orange,
                                                                color: rexTechTheme.colors.neutral.white
                                                            }
                                                        }}
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Add/Edit Modal */}
                <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                    <Modal.Header closeButton style={{
                        borderBottom: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                        backgroundColor: rexTechTheme.colors.neutral.lightGray
                    }}>
                        <Modal.Title style={{
                            color: rexTechTheme.colors.primary.dark,
                            fontWeight: rexTechTheme.typography.fontWeight.bold
                        }}>
                            {values.recycleItemId === '00000000-0000-0000-0000-000000000000' ? (
                                <>
                                    <Plus size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                                    Add New Recycle Item
                                </>
                            ) : (
                                <>
                                    <Edit2 size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                                    Edit Recycle Item
                                </>
                            )}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ padding: rexTechTheme.spacing.xl }}>
                        <form onSubmit={handleSubmit}>
                            <div style={{ marginBottom: rexTechTheme.spacing.lg }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: rexTechTheme.spacing.xs,
                                    color: rexTechTheme.colors.primary.dark,
                                    fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                    fontSize: rexTechTheme.typography.fontSize.regular
                                }}>
                                    Recycle Item Name
                                    <span style={{ color: rexTechTheme.colors.accent.orange, marginLeft: '4px' }}>*</span>
                                </label>
                                <input
                                    type="text"
                                    name="recycleItemName"
                                    value={values.recycleItemName}
                                    onChange={handleInput}
                                    style={{
                                        width: '100%',
                                        padding: rexTechTheme.spacing.md,
                                        border: `2px solid ${errors.recycleItemName ? rexTechTheme.colors.accent.orange : rexTechTheme.colors.neutral.gray}`,
                                        borderRadius: rexTechTheme.borderRadius.medium,
                                        fontSize: rexTechTheme.typography.fontSize.regular,
                                        transition: 'all 0.2s ease',
                                        outline: 'none',
                                        boxSizing: 'border-box',
                                        ':focus': {
                                            borderColor: rexTechTheme.colors.primary.main,
                                            boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                                        }
                                    }}
                                    placeholder="e.g., Plastic Bottles, Paper, Glass"
                                    maxLength="20"
                                />
                                {errors.recycleItemName && (
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: rexTechTheme.spacing.xs,
                                        marginTop: rexTechTheme.spacing.xs,
                                        color: rexTechTheme.colors.accent.orange,
                                        fontSize: rexTechTheme.typography.fontSize.small
                                    }}>
                                        <AlertCircle size={14} />
                                        <span>{errors.recycleItemName}</span>
                                    </div>
                                )}
                                <div style={{
                                    marginTop: rexTechTheme.spacing.xs,
                                    fontSize: rexTechTheme.typography.fontSize.xs,
                                    color: rexTechTheme.colors.neutral.darkGray,
                                    textAlign: 'right'
                                }}>
                                    {values.recycleItemName.length}/20 characters
                                </div>
                            </div>

                            <div style={{
                                display: 'flex',
                                gap: rexTechTheme.spacing.md,
                                marginTop: rexTechTheme.spacing.xl
                            }}>
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    style={{
                                        flex: 1,
                                        padding: rexTechTheme.spacing.md,
                                        backgroundColor: rexTechTheme.colors.neutral.lightGray,
                                        border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                                        color: rexTechTheme.colors.neutral.darkGray,
                                        borderRadius: rexTechTheme.borderRadius.medium,
                                        cursor: 'pointer',
                                        fontSize: rexTechTheme.typography.fontSize.regular,
                                        fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                        transition: 'all 0.2s ease',
                                        ':hover': {
                                            backgroundColor: rexTechTheme.colors.neutral.gray
                                        }
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={btnLoading}
                                    style={{
                                        flex: 1,
                                        padding: rexTechTheme.spacing.md,
                                        background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.main} 0%, ${rexTechTheme.colors.primary.light} 100%)`,
                                        color: rexTechTheme.colors.neutral.white,
                                        border: 'none',
                                        borderRadius: rexTechTheme.borderRadius.medium,
                                        fontSize: rexTechTheme.typography.fontSize.regular,
                                        fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                        cursor: btnLoading ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.3s ease',
                                        opacity: btnLoading ? 0.7 : 1,
                                        boxShadow: '0 4px 15px rgba(30, 132, 73, 0.3)',
                                        ':hover': btnLoading ? {} : {
                                            background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.dark} 0%, ${rexTechTheme.colors.primary.main} 100%)`,
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 6px 20px rgba(30, 132, 73, 0.4)'
                                        }
                                    }}
                                >
                                    {btnLoading ? (
                                        <>
                                            <div style={{
                                                width: '16px',
                                                height: '16px',
                                                border: `2px solid ${rexTechTheme.colors.neutral.white}`,
                                                borderTopColor: 'transparent',
                                                borderRadius: '50%',
                                                animation: 'spin 1s linear infinite',
                                                display: 'inline-block',
                                                marginRight: '8px',
                                                verticalAlign: 'middle'
                                            }} />
                                            Saving...
                                        </>
                                    ) : values.recycleItemId === '00000000-0000-0000-0000-000000000000' ? (
                                        <>
                                            <CheckCircle size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                                            Create Item
                                        </>
                                    ) : (
                                        <>
                                            <CheckCircle size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                                            Update Item
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </Modal.Body>
                </Modal>

                {/* Delete Confirmation Modal */}
                <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                    <Modal.Header closeButton style={{
                        borderBottom: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                        backgroundColor: rexTechTheme.colors.primary.lightest
                    }}>
                        <Modal.Title style={{
                            color: rexTechTheme.colors.accent.orange,
                            fontWeight: rexTechTheme.typography.fontWeight.bold
                        }}>
                            <AlertCircle size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                            Confirm Deletion
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ padding: rexTechTheme.spacing.xl }}>
                        <div style={{ textAlign: 'center', marginBottom: rexTechTheme.spacing.lg }}>
                            <XCircle size={48} style={{ 
                                color: rexTechTheme.colors.accent.orange,
                                marginBottom: rexTechTheme.spacing.md
                            }} />
                            <h4 style={{ 
                                color: rexTechTheme.colors.primary.dark,
                                marginBottom: rexTechTheme.spacing.sm
                            }}>
                                Delete Recycle Item?
                            </h4>
                            <p style={{ 
                                color: rexTechTheme.colors.neutral.darkGray,
                                lineHeight: 1.5
                            }}>
                                Are you sure you want to delete this recycle item? 
                                This action cannot be undone and may affect related data.
                            </p>
                        </div>
                        <div style={{
                            display: 'flex',
                            gap: rexTechTheme.spacing.md
                        }}>
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                style={{
                                    flex: 1,
                                    padding: rexTechTheme.spacing.md,
                                    backgroundColor: rexTechTheme.colors.neutral.lightGray,
                                    border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                                    color: rexTechTheme.colors.neutral.darkGray,
                                    borderRadius: rexTechTheme.borderRadius.medium,
                                    cursor: 'pointer',
                                    fontSize: rexTechTheme.typography.fontSize.regular,
                                    fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                    transition: 'all 0.2s ease',
                                    ':hover': {
                                        backgroundColor: rexTechTheme.colors.neutral.gray
                                    }
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDelete}
                                style={{
                                    flex: 1,
                                    padding: rexTechTheme.spacing.md,
                                    backgroundColor: rexTechTheme.colors.accent.orange,
                                    border: 'none',
                                    color: rexTechTheme.colors.neutral.white,
                                    borderRadius: rexTechTheme.borderRadius.medium,
                                    cursor: 'pointer',
                                    fontSize: rexTechTheme.typography.fontSize.regular,
                                    fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                    transition: 'all 0.2s ease',
                                    ':hover': {
                                        backgroundColor: '#d35400',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 4px 12px rgba(230, 126, 34, 0.3)'
                                    }
                                }}
                            >
                                <Trash2 size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                                Delete Item
                            </button>
                        </div>
                    </Modal.Body>
                </Modal>
            </div>
        </div>
    );
};

export default RecycleItems;